import React, { RefObject } from 'react';

import { Form, Input } from 'antd';
import { connect } from 'react-redux';
import { FormInstance } from 'antd/lib/form';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { RootState } from '@share/utils';
import { ICarsReviewBookState, setCarsDriver } from '@store/slices';
import { ILoginState, IMenuState } from '@share/store/slices';
import { ICarsDriver } from '@common-types';
import { mailFormat } from '@share/utils';

import './style.scss';
import { debounce, isEmpty } from 'lodash';

interface IMapStateToProps {
  carsReviewBookStore: ICarsReviewBookState;
  menuStore: IMenuState;
  loginStore: ILoginState;
}

interface IMapDispatchToProps {
  setDriver: (driver: ICarsDriver) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, WrappedComponentProps {
  formRef: RefObject<FormInstance>;
  handleEmailFormValidation: (emailForm: boolean) => void;
  ifEmailChanging: string | undefined;
}

interface IState {
  isFormValid: boolean;
}

class CarsConfirmationEmailComponent extends React.Component<IProps> {

  state: IState = {
    isFormValid: false,
  };

  checkFormValidity = debounce(() => {
    if (!this.props.formRef.current) return;

    // Get only errors from visible fields (excluding saveCard and country)
    const hasErrors = this.props.formRef.current
      .getFieldsError()
      .filter(({ name }) => name.length > 0) // Exclude specific fields
      .some(({ errors }) => errors.length > 0);

    // Get the current field values
    const allFields = this.props.formRef.current.getFieldsValue();

    // Manually exclude saveCard and country from validation
    const filteredFields = Object.keys(allFields)
      .reduce((obj, key) => {
        obj[key] = allFields[key];
        return obj;
      }, {});

    // Check if all visible fields have been touched and have values
    const allFieldsTouched = Object.values(filteredFields).every(value => value !== undefined && value !== "");

    // Update the button state
    this.setState({ isFormValid: !hasErrors && allFieldsTouched });
  }, 500);

  setEmailAddress = (value: string) => {
    const item = { ...this.props.carsReviewBookStore.driver, email: value };
    this.props.setDriver(item);
  };

  validateEmail = (e: { target: HTMLInputElement }) => {
    this.setEmailAddress(e.target.value);
  };

  onFocus = (e: { target: HTMLInputElement }) => {
    this.props.formRef.current?.setFieldsValue({ email: e.target.value });
  };

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (this.props.carsReviewBookStore.errorsField) {
      this.props.formRef.current?.validateFields().then();
    }

    if (prevState.isFormValid !== this.state.isFormValid) {
      this.props.handleEmailFormValidation(this.state.isFormValid);
    }

    if (prevProps.ifEmailChanging !== this.props.ifEmailChanging) {
      this.checkFormValidity();
    }
  }

  render(): React.ReactNode {
    const { intl, carsReviewBookStore, loginStore } = this.props;
    const { loadingBooking, driver } = carsReviewBookStore;
    const { account } = loginStore;

    const generalBorderRadius: any = account?.generalBorderRadius ? account?.generalBorderRadius : null;
    const style = !isEmpty(generalBorderRadius) ? { borderRadius: generalBorderRadius } : {};

    return (
      <div className="hotel-info cars-driver-email">
        <div className="cars-driver-email__container" style={{ width: '100%' }}>
          <div className="confirmation-email">
            <div className="confirmation-email__header">
              <h4 className="confirmation-email__title secondary-font">
                <FormattedMessage id="confirmation.email" />
              </h4>
              <p className="confirmation-email__description">
                <FormattedMessage id="enter.email.address" />
              </p>
            </div>
            <div className="confirmation-email__main-guest-info">
              <Form
                name="confirmation-email-rule"
                className="confirmation-email__rule"
                ref={this.props.formRef}
                onFieldsChange={this.checkFormValidity}
              >
                <Form.Item
                  name="email"
                  validateTrigger="onBlur"
                  rules={[
                    {
                      required: true,
                      pattern: new RegExp(mailFormat),
                      message: intl.formatMessage({ id: 'error.message.email.validation' }),
                    },
                  ]}
                >
                  <div className="confirmation-email__name-input-wrapper" style={style}>
                    <span className="confirmation-email__input-label">
                      <FormattedMessage id="email" />
                    </span>
                    <div className="confirmation-email__input">
                      <Input
                        placeholder={intl.formatMessage({ id: 'email.address' })}
                        value={driver?.email}
                        maxLength={255}
                        onChange={this.validateEmail}
                        onFocus={this.onFocus}
                        disabled={loadingBooking}
                      />
                    </div>
                  </div>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    carsReviewBookStore: state.carsReviewBookStore,
    menuStore: state.navigationMenuStore,
    loginStore: state.loginStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setDriver: setCarsDriver,
};

export const CarsConfirmationEmail = connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(CarsConfirmationEmailComponent));
